/**
 * Central API configuration.
 * Uses REACT_APP_API_HOST from environment.
 *
 * Local development: use .env.local with REACT_APP_API_HOST (e.g. http://localhost:5003).
 * CRA loads .env.local when running `npm start`.
 *
 * To avoid CORS in development: set REACT_APP_USE_PROXY=true in .env.local.
 * The dev server will proxy /api/* to REACT_APP_API_HOST.
 *
 * For builds: use build:dev, build:pre, build:prd which load .env.dev, .env.pre, .env.prd
 */

export function getApiUrl() {
  const useProxy = process.env.REACT_APP_USE_PROXY === 'true';
  const host = process.env.REACT_APP_API_HOST;

  // In development with proxy: use relative /api so requests go through dev server (avoids CORS)
  if (useProxy && process.env.NODE_ENV === 'development') {
    return '/api';
  }

  if (host) return host;
  console.error('REACT_APP_API_HOST is not set. Copy .env.local.example to .env.local and set your API host.');
  return '';
}

/**
 * Normalize backend-stored file/image URLs against REACT_APP_API_HOST.
 * Static uploads are often served at http(s)://host/uploads/... while the API lives at
 * http(s)://host/cloud-office-api — replacing only the hostname with the full API base
 * incorrectly produced .../cloud-office-api/uploads/...
 */
/**
 * True for object storage URLs (R2 / S3 public media hosts or signed links) that must be used as-is.
 * REACT_APP_MEDIA_HOST may list several hosts, comma separated (one per storage provider).
 */
export function isObjectStorageUrl(url) {
  if (!url || typeof url !== 'string' || !/^https?:\/\//i.test(url)) return false;
  const mediaHosts = (process.env.REACT_APP_MEDIA_HOST || '')
    .split(',')
    .map((h) => h.trim().replace(/\/$/, ''))
    .filter(Boolean);
  if (mediaHosts.some((h) => url === h || url.startsWith(`${h}/`))) return true;
  if (/[?&]X-Amz-Signature=/i.test(url)) return true;
  try {
    const { hostname } = new URL(url);
    return /\.r2\.(cloudflarestorage\.com|dev)$/i.test(hostname) || /(^|\.)s3[.-]([a-z0-9-]+\.)?amazonaws\.com$/i.test(hostname);
  } catch {
    return false;
  }
}

export function resolveImageUrl(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') return imageUrl;
  if (isObjectStorageUrl(imageUrl)) return imageUrl;
  const apiHost = getApiUrl();
  if (!apiHost) return imageUrl;
  const base = apiHost.replace(/\/$/, '');

  // Proxy dev base (e.g. /api): keep simple host swap
  if (!/^https?:\/\//i.test(base)) {
    return imageUrl.replace(/^https?:\/\/[^/]+/i, base);
  }

  let fileUrl;
  let baseUrl;
  try {
    fileUrl = new URL(imageUrl);
    baseUrl = new URL(base);
  } catch {
    return imageUrl.replace(/^https?:\/\/[^/]+/i, base);
  }

  const baseNorm = base.replace(/\/$/, '');
  const imgNorm = imageUrl.replace(/\/$/, '');
  if (imgNorm === baseNorm || imgNorm.startsWith(`${baseNorm}/`)) {
    return imageUrl;
  }

  // Files served at site root: /uploads/... — use API origin only, not /cloud-office-api prefix
  if (fileUrl.pathname.startsWith('/uploads/')) {
    return `${baseUrl.origin}${fileUrl.pathname}${fileUrl.search}${fileUrl.hash}`;
  }

  const basePath = (baseUrl.pathname || '/').replace(/\/$/, '') || '';
  if (basePath && basePath !== '/' && fileUrl.pathname.startsWith(`${basePath}/`)) {
    return `${baseNorm}${fileUrl.pathname.slice(basePath.length)}${fileUrl.search}${fileUrl.hash}`;
  }

  return imageUrl.replace(/^https?:\/\/[^/]+/i, base);
}
